import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Report } from "../../Redux/Slice/TaskMasterSlice";
import DynamicTable from "../../Components/Table/DynamicTable";
import * as XLSX from "xlsx";

const ReportPage = () => {
  const dispatch = useDispatch();
  const { ReportData } = useSelector((state) => state.TaskMaster);

  useEffect(() => {
    dispatch(Report());
  }, [dispatch]);

  // =======================
  // Excel Download Function
  // =======================
  const downloadExcel = (rows, fileName) => {
    if (!rows || rows.length === 0) {
      alert("No data available to download!");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);

    // Column width auto
    worksheet["!cols"] = Object.keys(rows[0] || {}).map((key) => {
      const maxLen = Math.max(
        key.length,
        ...rows.slice(0, 50).map((r) => String(r[key] || "").length)
      );
      return { wch: Math.min(Math.max(maxLen + 3, 12), 40) };
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  // Helper for dates
  const formatDateStr = (dateVal) => {
    if (!dateVal) return "-";
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? "-" : d.toLocaleDateString("en-IN");
  };

  // Helper for company name
  const getCompanyName = (comp) => {
    if (!comp) return "-";
    if (typeof comp === "string") return comp;
    return comp.WhaClientName || comp.CompanyName || comp.name || "-";
  };

  // =======================
  // Customer Request Rows
  // =======================
  const customerrequestRows =
    ReportData?.data?.map((item, index) => ({
      id: index + 1,
      tokan: item?.CallNo || "-",
      CompanyName: getCompanyName(item?.CompanyName),
      ContactPerson: item?.ContactPerName || item?.ContactPerson || "-",
      ContactPerNumber: item?.ContactPer || item?.ContactPerNumber || "-",
      Date: formatDateStr(item?.Date),
      Module: item?.Module?.module_name || item?.Module || "-",
      PresentStatus: item?.PresentStatus || "-",
      Requirement: item?.Requirement || "-",
      Solution: item?.Solution || "-",
      Benefit: item?.Benefit || "-",
      Project: item?.Project?.project_name || item?.Project || "-",
      Status: item?.Status || "-",
      InPutId: item?.InPutId?.Name || item?.InPutId || "-",
      SuportTeamId: item?.SuportTeamId?.Name || item?.SuportTeamId || "-",
    })) || [];

  const handleDownloadCustomerRequestExcel = () => {
    if (!customerrequestRows || customerrequestRows.length === 0) {
      alert("No data available to download!");
      return;
    }
    const exportData = customerrequestRows.map((r, index) => ({
      "Id": r.id || index + 1,
      "Call No.": r.tokan,
      "Company Name": r.CompanyName,
      "Date": r.Date,
      "Assign Name": r.InPutId,
    }));
    downloadExcel(exportData, "Customer_Request_Report");
  };

  const customerrequestColumns = [
    { field: "id", headerName: "Id", flex: 1 },
    { field: "tokan", headerName: "Call No.", flex: 1 },
    { field: "CompanyName", headerName: "Company Name", flex: 1 },
    { field: "Date", headerName: "Date", flex: 1 },
    { field: "InPutId", headerName: "Assign Name", flex: 1 },
  ];

  // =======================
  // Inhouse Rows
  // =======================
  const cInhousetRows =
    ReportData?.inhouse?.map((item, index) => ({
      id: index + 1,
      tokan: item?.CallNo || "-",
      CompanyName: getCompanyName(item?.CompanyName),
      Date: formatDateStr(item?.Date),
      PresentStatus: item?.PresentStatus || "-",
      Requirement: item?.Requirement || "-",
      Solution: item?.Solution || "-",
      Benefit: item?.Benefit || "-",
      Project: item?.Project?.project_name || item?.Project || "-",
      Status: item?.Status || "-",
      InPutId: item?.InPutId?.Name || item?.InPutId || "-",
      SuportTeamId: item?.SuportTeamId?.Name || item?.SuportTeamId || "-",
    })) || [];

  const handleDownloadInhouseExcel = () => {
    if (!cInhousetRows || cInhousetRows.length === 0) {
      alert("No data available to download!");
      return;
    }
    const exportData = cInhousetRows.map((r, index) => ({
      "Id": r.id || index + 1,
      "Call No.": r.tokan,
      "Company Name": r.CompanyName,
      "Date": r.Date,
      "Assign Name": r.InPutId,
    }));
    downloadExcel(exportData, "Inhouse_Report");
  };

  const InhouseColumns = [
    { field: "id", headerName: "Id", flex: 1 },
    { field: "tokan", headerName: "Call No.", flex: 1 },
    { field: "CompanyName", headerName: "Company Name", flex: 1 },
    { field: "Date", headerName: "Date", flex: 1 },
    { field: "InPutId", headerName: "Assign Name", flex: 1 },
  ];

  return (
    <>
      {/* ================= Customer Request ================= */}
      <div className="mb-10">
        <div className="flex justify-center">
          <h3 className="inline-block text-xl sm:text-2xl xl:text-3xl font-medium main_title_page text-white px-6 py-2 rounded shadow">
            Customer Request
          </h3>
        </div>

        <div className="home_table mt-5 overflow-x-auto">
          <div className="flex justify-end">
            <button
              onClick={handleDownloadCustomerRequestExcel}
              className="inline-flex font-medium main_title_page text-white px-2 py-2 rounded shadow"
            >
              Download Customer Request Excel
            </button>
          </div>

          <DynamicTable
            columns={customerrequestColumns}
            rows={customerrequestRows}
          />
        </div>
      </div>

      {/* ================= Inhouse ================= */}
      <div>
        {/* Title Center */}
        <div className="flex justify-center mb-3">
          <h3 className="inline-block text-xl sm:text-2xl xl:text-3xl font-medium main_title_page text-white px-6 py-2 rounded shadow">
            Inhouse
          </h3>
        </div>

        {/* Button Right */}
        <div className="flex justify-end mb-2">
          <button
            onClick={handleDownloadInhouseExcel}
            className="inline-flex font-medium main_title_page text-white px-2 py-2 rounded shadow"
          >
            Download Inhouse Excel
          </button>
        </div>

        {/* Table Full Width */}
        <div>
          <DynamicTable columns={InhouseColumns} rows={cInhousetRows} />
        </div>
      </div>

    </>
  );
};

export default ReportPage;
